import { useEffect, useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { PawPrint, Sparkle, X } from "@phosphor-icons/react";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { useIsStoreApp } from "@/lib/storeAppDetection";
import { track } from "@/lib/analytics";

const STORAGE_KEY = "pawlink_ea_welcome_seen";
const easeOut = [0.16, 1, 0.3, 1];

/**
 * Early Access welcome banner — Google Play launch cohort only.
 *
 * Renders ONCE per Android device, tastefully at the top of Home, to convey
 * that access is intentional and celebratory (Founding Member), NOT a symptom
 * of a broken payment system. Dismissible; never appears again.
 *
 * Shown when ALL of the following are true:
 *   - `useIsStoreApp()` returns true  (Google Play / Capacitor Android)
 *   - user is authenticated
 *   - user has NOT already dismissed it (localStorage `pawlink_ea_welcome_seen`)
 *
 * Explicitly hidden for:
 *   - anyone on the web (getpawlink.com)
 *   - already-Premium users (they see ProtectionBanner instead)
 *
 * No fake urgency, no countdowns, no fake user counts, no fake deadlines.
 * No mention of Paddle / Google Billing / payment problems.
 */
export default function EarlyAccessWelcome() {
    const { user } = useAuth();
    const { t } = useI18n();
    const isStoreApp = useIsStoreApp();
    const reduce = useReducedMotion();
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        if (!isStoreApp) return;
        if (!user) return;
        if (user.plan === "premium") return;      // Premium users see ProtectionBanner
        try {
            if (localStorage.getItem(STORAGE_KEY)) return;
        } catch { /* localStorage denied — still show */ }
        setVisible(true);
        track("early_access_welcome_shown");
    }, [isStoreApp, user]);

    const dismiss = () => {
        try { localStorage.setItem(STORAGE_KEY, new Date().toISOString()); } catch { /* noop */ }
        setVisible(false);
        track("early_access_welcome_dismissed");
    };

    return (
        <AnimatePresence>
            {visible && (
                <motion.div
                    key="ea-welcome"
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: reduce ? 0 : 0.55, ease: easeOut }}
                    data-testid="early-access-welcome"
                    className="relative mx-auto max-w-4xl mt-4 sm:mt-6 px-4"
                >
                    <div className="relative rounded-2xl border border-paw-terra/30 bg-gradient-to-r from-paw-terra/12 via-paw-terra/6 to-emerald-500/8 backdrop-blur px-4 py-3 sm:px-5 sm:py-4">
                        <div className="flex items-start gap-3">
                            <div className="w-9 h-9 rounded-2xl bg-paw-terra/18 ring-1 ring-paw-terra/30 flex items-center justify-center flex-shrink-0">
                                <PawPrint size={16} weight="fill" className="text-paw-terra" />
                            </div>
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <p
                                        data-testid="early-access-welcome-title"
                                        className="text-[14px] sm:text-[15px] font-semibold text-paw-ink dark:text-paw-bone leading-tight"
                                    >
                                        {t("subscription.ea_welcome_h")}
                                    </p>
                                    <span
                                        data-testid="early-access-welcome-chip"
                                        className="inline-flex items-center gap-1 rounded-full bg-paw-terra/15 text-paw-terra ring-1 ring-paw-terra/30 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider"
                                    >
                                        <Sparkle size={9} weight="fill" />
                                        {t("subscription.early_access_chip")}
                                    </span>
                                </div>
                                <p className="mt-1 text-[12px] sm:text-[13px] text-paw-ink2 dark:text-paw-bone/70 leading-snug">
                                    {t("subscription.ea_welcome_sub")}
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={dismiss}
                                data-testid="early-access-welcome-dismiss"
                                aria-label={t("subscription.ea_welcome_dismiss")}
                                className="flex-shrink-0 -mt-1 -me-1 w-8 h-8 rounded-full grid place-items-center text-paw-ink2/60 hover:text-paw-ink dark:text-paw-bone/50 dark:hover:text-paw-bone/90 hover:bg-paw-ink/5 dark:hover:bg-paw-bone/5 transition"
                            >
                                <X size={14} weight="bold" />
                            </button>
                        </div>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
